// 多路同步與同一時刻目前只能播雲端錄影（SPEC #vms-web-459 PLAY-03「在支援本地來源之前」，VMS UX 評審 2026-10-13 P0-6）：
// 選攝影機前先知道哪幾台有雲端錄影——這個月與上個月有沒有雲端錄影的日子（façade recording-days，跟回放月曆同一份）。
// 只問前 CLOUD_PROBE_MAX 台（範圍很大時不一次打上百個請求）；問不到的當「不知道」，照樣可選。
import { useQueries } from '@tanstack/react-query';
import { KinioError } from '@kinio/web-sdk';
import { cloudRecordingDays } from '../../lib/facade';
import { browserTz } from '../../lib/tz';
import type { VmsDevice } from '../../lib/devices';

export const CLOUD_PROBE_MAX = 40;

/** YYYY-MM 往前 n 個月。 */
export function monthsBack(now: Date, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export type CloudState = 'yes' | 'no' | 'unknown';

/** 範圍整體：全部問完而且都沒有雲端錄影 = none（頁頂說明、不讓人選完才看到一整排空格）。 */
export function cloudSummary(states: CloudState[]): 'none' | 'some' | 'unknown' {
  if (!states.length) return 'unknown';
  if (states.some((s) => s === 'yes')) return 'some';
  return states.every((s) => s === 'no') ? 'none' : 'unknown';
}

export function useCloudCams(cameras: VmsDevice[]): { stateOf: (key: string) => CloudState; summary: 'none' | 'some' | 'unknown'; loading: boolean } {
  const probe = cameras.slice(0, CLOUD_PROBE_MAX);
  const months = monthsBack(new Date(), 2);
  const tz = browserTz();
  const qs = useQueries({
    queries: probe.map((c) => ({
      queryKey: ['cloud-days-recent', c.homeId, c.aid, months.join(), tz],
      staleTime: 10 * 60_000,
      retry: false,
      queryFn: async () => {
        try {
          const all = await Promise.all(months.map((m) => cloudRecordingDays(c.apiEndpoint, c.homeId, c.aid, m, tz)));
          return all.some((d) => d.length > 0);
        } catch (e) {
          // 沒有雲端錄影權益 / 席次、沒有權限 = 沒有雲端錄影；其他錯誤（網路）= 不知道
          if (e instanceof KinioError && [402, 403, 404].includes(e.status)) return false;
          throw e;
        }
      },
    })),
  });
  const m = new Map<string, CloudState>();
  probe.forEach((c, i) => {
    const q = qs[i];
    m.set(c.key, q.isSuccess ? (q.data ? 'yes' : 'no') : 'unknown');
  });
  const states = probe.map((c) => m.get(c.key) ?? 'unknown');
  return {
    stateOf: (k) => m.get(k) ?? 'unknown',
    summary: probe.length < cameras.length && !states.includes('yes') ? 'unknown' : cloudSummary(states),
    loading: qs.some((q) => q.isLoading),
  };
}

/** 錄影政策回應 → 雲端只存事件片段（雲錄生效中、模式是事件）。 */
export function eventOnlyCloud(p: { cloud?: { effective?: boolean; effective_mode?: string | null } | null } | undefined): boolean {
  return !!p?.cloud?.effective && p.cloud.effective_mode === 'event';
}
